import { type FC } from 'react';

import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { useDrugKey } from '../../../../hooks/use-drug-key';
import { useFlags } from '../../../../hooks/use-flags';
import { usePriceCardContent } from '../../../../hooks/use-price-card-content-props';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import type { PriceCardV2Flag } from '../../../../types/non-boolean-flags';
import { hasIncentiveSavings } from '../../../../util/offers/has-incentive-savings';
import { isIncentiveOffer } from '../../../../util/offers/is-incentive-offer';
import { PricingCardV2 } from '../common/PricingCardV2';
import type { PricingCardProps } from '../common/shared';
import { usePriceQualifier } from '../common/use-price-qualifier';

import { SelfPayPricingContent } from './SelfPayPricingContent';
import { useSelfPayPricing } from './use-self-pay-pricing';

export const SelfPayPricing: FC<PricingCardProps> = ({ offer }) => {
    const { allOffers, medication } = useFillPrescription();
    const { enablePriceCardV2 } = useFlags();
    const drugKey = useDrugKey(medication);

    const terms = useProgramTerms(medication, 'self-pay');
    const hasIncentiveProgram =
        !!terms?.incentiveProgram && !!allOffers.find(isIncentiveOffer);

    const { patientPay, priceType, savings } = useSelfPayPricing(offer);

    // hasIncentiveProgram only tells us an Incentive-typed offer exists — some
    // doses carry one with no real discount (Incentive === Cash) purely to trigger
    // the purchase-certification disclaimer below. Only a genuine discount (see
    // hasIncentiveSavings) should route to the incentive terms/link.
    const hasJourneyProgram =
        hasIncentiveProgram && hasIncentiveSavings(offer, savings);
    const priceQualifier = usePriceQualifier({
        medication,
        patientPay,
        priceType,
    });

    const cardContentOverride = usePriceCardContent({
        medication,
        headlinePriceType: priceType,
        patientCost: patientPay,
    });

    const priceCardFlag = enablePriceCardV2 as PriceCardV2Flag;

    const termsDisclaimer =
        (!!offer && isIncentiveOffer(offer)) || (!offer && hasIncentiveProgram)
            ? (terms?.incentiveProgram?.disclaimer ?? '')
            : '';

    if (
        !!drugKey &&
        priceCardFlag.drugKeys.includes(drugKey) &&
        cardContentOverride?.pricingCard
    ) {
        return (
            <PricingCardV2
                pricingCard={cardContentOverride.pricingCard}
                priceNote={cardContentOverride.priceNote}
            />
        );
    }

    return (
        <SelfPayPricingContent
            hasIncentiveProgram={hasJourneyProgram}
            medication={medication}
            offer={offer}
            patientPay={patientPay}
            priceType={priceType}
            priceQualifier={priceQualifier}
            savings={savings}
            termsDisclaimer={termsDisclaimer}
        />
    );
};
